import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  useTheme,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Tooltip,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import { SelectChangeEvent } from '@mui/material/Select';
import { userInitial } from "../../../Shared-ui/Store/UserReducer/UserReducer";
import { User } from "../../../Shared-ui/Types/User";
import { StartLoading, StopLoading } from "../../../Shared-ui/Store/LoadingReducer/LoaderActions";
import { useDispatch } from "react-redux";
import { ShowError, ShowSuccess } from "../../../Shared-ui/Store/MessageReducer/MsgActions";
import BaseInstance from "../../../Services/AxiosInstance/BaseInstance";
import { ADMIN_ORGANIZATIONS, ADMIN_USERS } from "../../../Services/Endpoint";
import { Organization } from "../../../Shared-ui/Types/Admin";
import { NotSelected } from "../../../Constant/Setting";
import { isOnlyAlphabetsNumbers } from "../../../Shared-ui/utils/Common/Common";
import { ValidateEmailIsOrg } from "../../../Shared-ui/utils/Auth/AuthHelpers";
import { ValidateIsEmail } from "../../../Shared-ui/utils/Auth/AuthHelpers"; // <--- Add ValidateIsEmail here

interface MyComponentProps {
  open: boolean;
  handleClose: () => void;
  user: User | null
}


const CreateUser: React.FC<MyComponentProps> = (props) => {
  const errorInitialUser = {
    email: { error: "", value: false },
    full_name: { error: "", value: false },
    role: { error: "", value: false },
    organization_id: { error: "", value: false }

  }
  const dispatch = useDispatch();
  const [user, setUser] = React.useState({ ...userInitial });
  const [userErr, setUserErr] = React.useState(errorInitialUser);
  const [organizations, setOrganizations] = React.useState([]);

  const [isEditing, setIsEditing] = useState(false)

  const [limit, setLimit] = useState(999)

  useEffect(() => {
    if (props.user) {
      setUser(props.user)
      setIsEditing(true)
      getOrganizations()
    } else {
      setIsEditing(false)
      getOrganizations()
    }
  }, [props.open])



  const handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent) => {
    const { name, value } = event.target;
    setUser(preVal => ({ ...preVal, [name]: value }))
  };

  const handleClick = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, checked: any) => {
    const { name, value } = event.target;
    setUser(preVal => ({ ...preVal, [name]: checked }))
  };

  const getOrganizations = async () => {
    try {
      dispatch(StartLoading())
      const data = await BaseInstance.get(`${ADMIN_ORGANIZATIONS}?skip=${0}&limit=${limit}`).then(res => res?.data)
      // data.sort((a: { created_at: string }, b: { created_at: string }) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
      setOrganizations(data)
    } catch (err: any) {
      dispatch(ShowError(err?.response?.data?.statusText));
    } finally {
      dispatch(StopLoading())
    }
  }

  const createUser = async () => {
    try {
      if (!validateUser()) return
      dispatch(StartLoading())
      const data = await BaseInstance.post(`${ADMIN_USERS}`, user).then(res => res?.data)
      dispatch(ShowSuccess("User created"));
      handleClose()
    } catch (err: any) {
      dispatch(ShowError(err?.response?.data?.detail));
    } finally {
      dispatch(StopLoading())
    }
  };

  const updateUser = async () => {
    try {
      if (!validateUser()) return;
      dispatch(StartLoading())
      const data = await BaseInstance.put(`${ADMIN_USERS}/${user.id}`, user).then(res => res?.data)
      dispatch(ShowSuccess("User updated"));
      handleClose()
    } catch (err: any) {
      dispatch(ShowError(err?.response?.data?.detail));
    } finally {
      dispatch(StopLoading())
    }
  };

  const validateUser = () => {
    var error = 0

    const setError = (field: string, condition: boolean, message: string) => {
      if (condition) {
        setUserErr(prev => ({
          ...prev,
          [field]: { error: message, value: true }
        }));
        error++;
      } else {
        setUserErr(prev => ({
          ...prev,
          [field]: { error: "", value: false }
        }));
      }
    }
    setError(
      "full_name",
      !user.full_name.trim() ||
        !isOnlyAlphabetsNumbers(user.full_name.trim()) ||
        user.full_name.trim().length < 2 ||
        !/[a-zA-Z]/.test(user.full_name.trim()),
      "Please enter a name with at least 2 characters (A-z)"
    );
    // setError("email", (!ValidateEmailIsOrg(user.email) || !user.email.trim()), "Please enter the email");
    // setError("role", (!user.role || !user.role.trim()), "Please enter the role a-Z");
    setError("organization_id", (!user.organization_id || user.organization_id === NotSelected), "Please select the organization");

  if (!ValidateIsEmail(user.email)) {
    setError("email", true, "Please enter a valid email");
  } else if (!ValidateEmailIsOrg(user.email)) {
    setError("email", true, "Personal emails are not allowed");
  } else {
    setError("email", false, "");
  }
    return error > 0 ? false : true
  }

  const handleClose = () => {
    setUser({ ...userInitial })
    setUserErr({...errorInitialUser})
    setIsEditing(false)
    props.handleClose()
  }

  return (
    <Dialog open={props.open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle
        sx={{
          fontWeight: 600,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          p: 2, // Use direct value instead of theme.spacing
        }}
      >
        <Box>
          {!isEditing ? "Create" : "Update"} User
          <Typography
            variant="subtitle2"
            color="textSecondary"
            sx={{ mt: 0.5 }}
          >
            {!isEditing ? "Add a new user to the system" : "Edit the user details"}
          </Typography>
        </Box>
        <IconButton onClick={handleClose} sx={{ ml: 1 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        <Box
          component="form"
          // onSubmit={createUser}
          sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}
        >
          <FormControl fullWidth variant="outlined" sx={{ mt: 1,mb: 1 }} error={userErr.full_name.value}>
            <TextField
              fullWidth
              label="Full Name"
              name="full_name"
              error={userErr.full_name.value}
              helperText={userErr.full_name.error}
              value={user.full_name}
              onChange={handleChange}
              placeholder="John Doe"
              inputProps={{ maxLength: 50 }}
            />
          </FormControl>
          <FormControl fullWidth variant="outlined" sx={{ mb: 1 }} error={userErr.email.value}>
            <TextField
              fullWidth
              label="Email"
              name="email"
              type="email"
              error={userErr.email.value}
              helperText={userErr.email.error}
              value={user.email}
              onChange={handleChange}
              placeholder="john@example.com"
              inputProps={{ maxLength: 50 }}
            />
          </FormControl>
          <FormControl fullWidth error={userErr.organization_id.value}>
            <InputLabel>Organization</InputLabel>
            <Select
              value={user.organization_id}
              name="organization_id"
              label="Organization"
              onChange={handleChange}
              error={userErr.organization_id.value}
               MenuProps={{
                PaperProps: {
                  style: {
                    maxWidth: '300px', 
                    width: '100%', 
                    maxHeight: '200px', 
                    overflowY: 'auto', 
                  },
                },
              }}
            >
              <MenuItem value={user.organization_id || NotSelected}>Select organization</MenuItem>
              {organizations.map((org: Organization, index) => (
                // <Tooltip title={org.name} key={org.id} placement="top">
                  <MenuItem value={org.id}>
                  <Box
                      sx={{
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        maxWidth: 'calc(100% - 32px)', 
                      }}
                    >
                  {org.name}
                  </Box>
                  </MenuItem>
                // </Tooltip>
              ))}
            </Select>
          </FormControl>

          <Box sx={{ display: "flex", gap: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Role</InputLabel>
              <Select
                value={user.role}
                name="role"
                label="Role"
                onChange={handleChange}
              >
                <MenuItem value="user">user</MenuItem>
                <MenuItem value="admin">Admin</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Subscription</InputLabel>
              <Select
                value={user.subscription}
                name="subscription"
                label="Subscription"
                onChange={handleChange}
              >
                <MenuItem value="standard">Standard</MenuItem>
                <MenuItem value="premium">Premium</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {/* <FormControl fullWidth>
            <InputLabel>Teams (Optional)</InputLabel>
            <Select
              value={formData.teams}
              name="teams"
              label="Teams (Optional)"
              onChange={handleChange}
            >
              <MenuItem value="">Select teams</MenuItem>
              <MenuItem value="team1">Team 1</MenuItem>
              <MenuItem value="team2">Team 2</MenuItem>
            </Select>
          </FormControl> */}

          <Box sx={{ display: "flex", gap: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={user.is_active}
                  onChange={handleClick}
                  name="is_active"
                />
              }
              label="Active"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={user.is_superuser}
                  onChange={handleClick}
                  name="is_superuser"
                />
              }
              label="Superuser"
            />
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} color="primary" variant="outlined" size="large"  sx={{textTransform: "capitalize",}}>
          Cancel
        </Button>
        <Button variant="contained" color="primary" type="submit" size="large" sx={{textTransform: "capitalize",}}
          onClick={isEditing ? () => updateUser() : () => createUser()}
        >
          {!isEditing ? "Create" : "Update"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateUser;
